/**
 * `EvaluatedTimeRecord[]` -> `PayrollBuildResult` — Time Evaluation Phase 4b.
 *
 * ── Why this file exists: to prevent two divergent truths ─────────────────────
 * There are two serializers on the payroll last mile and they read DIFFERENT
 * shapes:
 *
 *   `serializePayrollBatch.ts`  — the vendor-neutral QA preview. Reads
 *                                 `PayrollBuildResult` (REG/OT/DT/LV only,
 *                                 premium-BLIND). Its own docstring already
 *                                 frames it as the preview stage. KEPT, not
 *                                 replaced.
 *   `serializeAdpPaydata.ts`    — the real ADP file. Reads
 *                                 `EvaluatedTimeRecord[]` (premium-AWARE).
 *
 * If the preview kept reading `buildPayrollBatch` (which recomputes OT from raw
 * time entries with its own rounding) while the ADP file read the engine, the
 * two would drift and nobody would notice until a worker complained. Routing
 * BOTH through this mapper makes them the same numbers by construction: one
 * evaluation, two views.
 *
 * ── The contract change this needed ───────────────────────────────────────────
 * `PayrollEarning.earningsCode?: string` was added to
 * `integrations/adapterContracts.ts` (additive, optional, back-compatible). The
 * canonical `code` stays one of REG/OT/DT/LV so every existing consumer keeps
 * totalling correctly; `earningsCode` carries the specific internal code
 * ('SHIFT-3RD', 'HOL-WORKED', …) that the canonical set cannot name.
 *
 * ⚠️ CONSEQUENCE, stated plainly: a premium line is bucketed under the canonical
 * code of the hours it sits on. On an 8h night shift with 4 premium hours,
 * `code: 'REG'` carries TWO rows — 8 base hours and 4 uplift-tagged hours — so
 * the preview's `reg_hours` column reads 12. That is the same 12 hour-cells the
 * ADP file carries (Reg Hours 8 + Hours 3 Amount 4), which is exactly the point.
 * It is not double payment: ADP prices the premium code as an uplift factor, not
 * as base pay. Split them on `earningsCode` when a surface needs them apart.
 *
 * ── What this mapper does NOT carry ───────────────────────────────────────────
 * `PayrollEarning` has no dollar field, so flat premiums (lead, hazard, jury,
 * bereavement, NY spread-of-hours) arrive as `hours: 0` rows and their DOLLARS
 * are invisible in the vendor-neutral preview. That is a pre-existing limit of
 * the hours-only preview shape, not something introduced here. The ADP file
 * carries them correctly in its Earnings slots. Do not "fix" it by putting
 * dollars in `hours`.
 *
 * PURE. No I/O.
 */
import type {
  PayrollBatchLine,
  PayrollEarning,
  PayrollEarningCode,
  PayrollWorker,
} from '@/integrations/adapterContracts'
import type { EarningsCategory } from '../types/manufacturing-time.types'
import type { EvaluatedTimeRecord } from '../types/timeEvaluation.types'
import type {
  PayrollBuildBlocker,
  PayrollBuildResult,
  PayrollBuildTotals,
  PayrollEmployeeLike,
  PayrollPeriod,
} from './buildPayrollBatch'

/**
 * 2dp, NOT `buildPayrollBatch`'s 1dp `r1`.
 *
 * Deliberate divergence: the whole job of this mapper is to tie out against the
 * ADP file, which is 2dp. Rounding to 1dp here would move up to 3 minutes per
 * worker per line between the preview and the file and make the parity test a
 * lie. Same `PayrollBuildTotals` type, tighter precision.
 */
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** Earnings categories that are paid LEAVE, not worked time. */
const LEAVE_CATEGORIES: ReadonlySet<EarningsCategory> = new Set<EarningsCategory>([
  'pto',
  'sick',
  'holiday',
  'jury_duty',
  'bereavement',
])

const BUCKET_TO_CODE: Record<'regular' | 'overtime' | 'double_time', PayrollEarningCode> = {
  regular: 'REG',
  overtime: 'OT',
  double_time: 'DT',
}

/**
 * Which canonical bucket an evaluated record belongs to.
 *
 * ORDER MATTERS. `bucket` is checked FIRST because a record that carries one is
 * worked time by definition — a holiday-WORKED premium has
 * `earningsCategory: 'holiday'` AND `bucket: 'regular'`, and category-first
 * would file those worked hours as leave.
 */
export function canonicalPayrollCode(record: EvaluatedTimeRecord): PayrollEarningCode {
  if (record.bucket) return BUCKET_TO_CODE[record.bucket]
  if (LEAVE_CATEGORIES.has(record.earningsCategory)) return 'LV'
  if (record.earningsCategory === 'overtime') return 'OT'
  if (record.earningsCategory === 'double_time') return 'DT'
  // Everything else with no bucket — a flat premium, a statutory premium, a
  // call-in minimum — is straight-time-adjacent money. REG is the only honest
  // home in a four-code vocabulary, and `earningsCode` names what it really is.
  return 'REG'
}

export interface EvaluatedToPayrollBatchArgs {
  records: readonly EvaluatedTimeRecord[]
  period: PayrollPeriod
  /**
   * employeeId -> payroll crosswalk. Missing => `no_worker_record` blocker;
   * present with a blank `payrollId` => `no_payroll_id`. Same vocabulary and
   * same all-or-nothing rule as `buildPayrollBatch`.
   */
  workers: Record<string, PayrollWorker | undefined>
  /** employeeId -> employee-like, used ONLY for a fallback display name. */
  employees?: Record<string, PayrollEmployeeLike | undefined>
}

interface EarningKey {
  code: PayrollEarningCode
  earningsCode: string
  leaveType?: string
}

function earningKeyOf(record: EvaluatedTimeRecord): EarningKey {
  const code = canonicalPayrollCode(record)
  const key: EarningKey = {
    code,
    earningsCode: (record.earningsCodeInternal ?? '').trim(),
  }
  // No AWART reaches this layer (the engine resolves an absence to a category —
  // see BUILD_LOG Phase 2 open item 1, "AWART -> EarningsCategory map does not
  // exist"). The category is the most specific leave label available today; when
  // that map lands, this is the line that should carry the real AWART.
  if (code === 'LV') key.leaveType = record.sapAbsenceType || record.earningsCategory
  return key
}

/**
 * Map evaluated records into the vendor-neutral `PayrollBuildResult` the
 * existing preview/partition/blocker machinery already speaks.
 *
 * Deterministic: lines sorted by payrollId (matching `buildPayrollBatch`),
 * earnings sorted by (canonical code order, then internal code) so a rerun of
 * the same period produces a byte-identical preview.
 */
export function evaluatedToPayrollBatch(args: EvaluatedToPayrollBatchArgs): PayrollBuildResult {
  const { period, workers } = args
  const employees = args.employees ?? {}

  const inPeriod = (args.records ?? []).filter(
    (r) => r.date >= period.start && r.date <= period.end,
  )

  const byEmployee = new Map<string, EvaluatedTimeRecord[]>()
  for (const r of inPeriod) {
    const arr = byEmployee.get(r.employeeId) ?? []
    arr.push(r)
    byEmployee.set(r.employeeId, arr)
  }

  const lines: PayrollBatchLine[] = []
  const blockers: PayrollBuildBlocker[] = []
  const totals: PayrollBuildTotals = {
    exportableWorkers: 0,
    blockedWorkers: 0,
    regular: 0,
    overtime: 0,
    doubletime: 0,
    leave: 0,
    totalHours: 0,
  }

  const CODE_ORDER: PayrollEarningCode[] = ['REG', 'OT', 'DT', 'LV']

  for (const employeeId of [...byEmployee.keys()].sort()) {
    const records = byEmployee.get(employeeId)!

    // Aggregate to one earnings row per (canonical code x internal code). The
    // engine emits one record per (date, code, kind); a period-level payroll
    // line collapses the dates and the kinds, never the codes.
    const agg = new Map<string, { key: EarningKey; hours: number }>()
    for (const r of records) {
      const key = earningKeyOf(r)
      const mapKey = `${key.code}|${key.earningsCode.toUpperCase()}|${key.leaveType ?? ''}`
      const cur = agg.get(mapKey) ?? { key, hours: 0 }
      cur.hours += r.hours
      agg.set(mapKey, cur)
    }

    const earnings: PayrollEarning[] = [...agg.values()]
      .sort(
        (a, b) =>
          CODE_ORDER.indexOf(a.key.code) - CODE_ORDER.indexOf(b.key.code) ||
          a.key.earningsCode.localeCompare(b.key.earningsCode),
      )
      .map((e) => {
        const row: PayrollEarning = { code: e.key.code, hours: round2(e.hours) }
        if (e.key.earningsCode) row.earningsCode = e.key.earningsCode
        if (e.key.leaveType) row.leaveType = e.key.leaveType
        return row
      })

    const totalHours = round2(records.reduce((sum, r) => sum + r.hours, 0))

    const worker = workers[employeeId]
    const workerName = worker?.name?.trim() || employees[employeeId]?.name || employeeId

    if (!worker) {
      blockers.push({ employeeId, workerName, reason: 'no_worker_record', hours: totalHours })
      totals.blockedWorkers += 1
      continue
    }
    if (!worker.payrollId) {
      blockers.push({ employeeId, workerName, reason: 'no_payroll_id', hours: totalHours })
      totals.blockedWorkers += 1
      continue
    }

    lines.push({
      workerExternalId: employeeId,
      payrollId: worker.payrollId,
      sapEmployeeNo: worker.sapEmployeeNo ?? null,
      payrollTenant: worker.payrollTenant ?? null,
      earnings,
      totalHours,
    })
    totals.exportableWorkers += 1
    for (const e of earnings) {
      if (e.code === 'REG') totals.regular = round2(totals.regular + e.hours)
      else if (e.code === 'OT') totals.overtime = round2(totals.overtime + e.hours)
      else if (e.code === 'DT') totals.doubletime = round2(totals.doubletime + e.hours)
      else if (e.code === 'LV') totals.leave = round2(totals.leave + e.hours)
    }
    totals.totalHours = round2(totals.totalHours + totalHours)
  }

  lines.sort((a, b) => a.payrollId.localeCompare(b.payrollId))
  blockers.sort((a, b) => a.employeeId.localeCompare(b.employeeId))

  return { batch: { period, lines }, blockers, totals }
}

/**
 * The inverse leg: `PayrollBatchLine.earnings` -> minimal `EvaluatedTimeRecord`s
 * the ADP serializer can consume.
 *
 * ⚠️ LOSSY, and only used where nothing better is available. A `PayrollBatch` is
 * hours-only and period-level, so this reconstruction loses every DOLLAR (flat
 * premiums come back as zero-hour, zero-dollar rows and therefore drop out of
 * the file's Earnings slots), loses per-day detail, and loses rate/multiplier
 * provenance. It exists so `PayrollAdapter.pushPayrollBatch(batch)` — whose
 * signature is fixed by `adapterContracts.ts` and only ever receives a
 * `PayrollBatch` — can still produce a file.
 *
 * Any caller that HAS the `EvaluatedTimeRecord[]` must pass those directly
 * (`AdpPayrollAdapter.buildPaydataFile`), not round-trip through here.
 */
export function payrollBatchLinesToRecords(
  lines: readonly PayrollBatchLine[],
  period: PayrollPeriod,
): EvaluatedTimeRecord[] {
  const CATEGORY_FOR_CODE: Record<PayrollEarningCode, EarningsCategory> = {
    REG: 'regular',
    OT: 'overtime',
    DT: 'double_time',
    LV: 'pto',
  }
  const out: EvaluatedTimeRecord[] = []
  for (const line of lines) {
    for (const e of line.earnings) {
      const internal = e.earningsCode?.trim() || e.code
      out.push({
        id: `${line.workerExternalId}|${period.end}|${internal}|base`,
        employeeId: line.workerExternalId,
        pernr: line.sapEmployeeNo ?? null,
        // Period-level reconstruction: everything is stamped on the period end
        // date, which is correct for a `rowGranularity: 'period'` profile and
        // WRONG for a 'day' profile. Documented, not silently papered over.
        date: period.end,
        kind: 'base',
        earningsCategory:
          (e.leaveType as EarningsCategory | undefined) ?? CATEGORY_FOR_CODE[e.code] ?? 'other',
        earningsCodeInternal: internal,
        hours: e.hours,
        rate: 0,
        multiplier: 1,
        amountDollars: 0,
        provenance: {
          stream: 'office',
          sourceRecordIds: [],
          blockIds: [],
          ruleCodes: [],
          matchedWindows: [],
          stacking: [],
          note: 'reconstructed from a PayrollBatch — dollars and per-day detail are not recoverable',
        },
      })
    }
  }
  return out
}
